import { useState, useEffect, useReducer, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePreferences } from '../contexts/PreferencesContext';
import api from '../services/api';
import SimplifiedCameraGrid from '../components/SimplifiedCameraGrid';
import OptimizedCameraStream from '../components/OptimizedCameraStream';
import BreadcrumbNavigation from '../components/BreadcrumbNavigation';
import FloatingControls from '../components/FloatingControls';
import {
  Monitor,
  Settings,
  Layout,
  Eye,
  X,
  Maximize2,
  Grid,
  Minimize2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

// Reducer for managing complex state efficiently
const initialState = {
  cameras: [],
  filteredCameras: [],
  loading: true,
  error: null,
  searchTerm: '',
  selectedCamera: null,
  showFullscreenModal: false,
  gridLayout: 'auto',
  thumbnailKeys: {},
  streamingCameras: new Set()
};

const cameraReducer = (state, action) => {
  switch (action.type) {
    case 'SET_CAMERAS':
      return {
        ...state,
        cameras: action.payload,
        loading: false,
        error: null
      };
    
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload
      };
    
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        loading: false
      };
    
    case 'SET_SEARCH_TERM':
      return {
        ...state,
        searchTerm: action.payload
      };
    
    case 'SET_SELECTED_CAMERA':
      return {
        ...state,
        selectedCamera: action.payload
      };
    
    case 'SET_FULLSCREEN_MODAL':
      return {
        ...state,
        showFullscreenModal: action.payload
      };
    
    case 'SET_GRID_LAYOUT':
      return {
        ...state,
        gridLayout: action.payload
      };
    
    case 'REFRESH_THUMBNAIL':
      return {
        ...state,
        thumbnailKeys: {
          ...state.thumbnailKeys,
          [action.payload]: (state.thumbnailKeys[action.payload] || 0) + 1
        }
      };
    
    case 'ADD_STREAMING_CAMERA':
      return {
        ...state,
        streamingCameras: new Set([...state.streamingCameras, action.payload])
      };
    
    case 'REMOVE_STREAMING_CAMERA':
      const newStreamingCameras = new Set(state.streamingCameras);
      newStreamingCameras.delete(action.payload);
      return {
        ...state,
        streamingCameras: newStreamingCameras
      };
    
    default:
      return state;
  }
};

const OptimizedCameraMonitoring = () => {
  const { hasPermission } = useAuth();
  const { preferences, updateNestedPreference } = usePreferences();
  const [state, dispatch] = useReducer(cameraReducer, initialState);

  // Memoized filtered cameras based on search
  const filteredCameras = useMemo(() => {
    if (!state.searchTerm || state.searchTerm.trim() === '') {
      return state.cameras;
    }

    const searchLower = state.searchTerm.toLowerCase().trim();
    const searchWords = searchLower.split(/\s+/).filter(word => word.length > 0);

    return state.cameras.filter(camera => {
      const searchableText = [
        camera.name || '',
        camera.description || '',
        camera.location?.exactLocation || '',
        camera.location?.city || '',
        camera.location?.state || '',
        camera.location?.country || '',
        camera.ipAddress || '',
        camera.port || '',
        camera.username || '',
        camera.isOnline ? 'online' : 'offline',
        camera.isActive ? 'active' : 'inactive',
        camera.trackNumber ? `track ${camera.trackNumber}` : '',
        camera.assignedUsers?.map(user => user.name || user.username || '').join(' ') || ''
      ].join(' ').toLowerCase();

      return searchWords.every(word => searchableText.includes(word));
    });
  }, [state.cameras, state.searchTerm]);

  // Load cameras with error handling
  const loadCameras = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await api.get('/cameras');
      
      if (response.data.success) {
        dispatch({ type: 'SET_CAMERAS', payload: response.data.data });
        console.log(`✅ Loaded ${response.data.data.length} cameras`);
      } else {
        throw new Error(response.data.message || 'Failed to load cameras');
      }
    } catch (error) {
      console.error('Failed to load cameras:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
      toast.error('Failed to load cameras');
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadCameras();
  }, [loadCameras]);

  // Handle camera actions
  const handleCameraClick = useCallback((camera) => {
    dispatch({ type: 'SET_SELECTED_CAMERA', payload: camera });
    dispatch({ type: 'SET_FULLSCREEN_MODAL', payload: true });
    dispatch({ type: 'ADD_STREAMING_CAMERA', payload: camera._id });
  }, []);

  const handleCloseFullscreen = useCallback(() => {
    dispatch({ type: 'SET_FULLSCREEN_MODAL', payload: false });
    setTimeout(() => {
      if (state.selectedCamera) {
        dispatch({ type: 'REMOVE_STREAMING_CAMERA', payload: state.selectedCamera._id });
        dispatch({ type: 'SET_SELECTED_CAMERA', payload: null });
      }
    }, 300); // Delay to allow modal close animation
  }, [state.selectedCamera]);

  const handleEditCamera = useCallback((camera) => {
    // Navigate to edit camera page or open edit modal
    console.log('Edit camera:', camera.name);
    toast.success('Edit camera functionality would go here');
  }, []);

  const handleDeleteCamera = useCallback(async (cameraId) => {
    if (!window.confirm('Are you sure you want to delete this camera?')) {
      return;
    }

    try {
      await api.delete(`/cameras/${cameraId}`);
      toast.success('Camera deleted successfully');
      loadCameras(); // Reload cameras
    } catch (error) {
      console.error('Failed to delete camera:', error);
      toast.error('Failed to delete camera');
    }
  }, [loadCameras]);

  const handleTestConnection = useCallback(async (cameraId) => {
    try {
      toast.loading('Testing connection...', { id: 'test-connection' });
      const response = await api.post(`/cameras/${cameraId}/test`);
      
      if (response.data.success) {
        toast.success('Connection test successful', { id: 'test-connection' });
        // Refresh thumbnail after successful test
        dispatch({ type: 'REFRESH_THUMBNAIL', payload: cameraId });
      } else {
        toast.error('Connection test failed', { id: 'test-connection' });
      }
    } catch (error) {
      console.error('Connection test failed:', error);
      toast.error('Connection test failed', { id: 'test-connection' });
    }
  }, []);

  const handleAssignCamera = useCallback((camera) => {
    console.log('Assign camera:', camera.name);
    toast.success('Assign camera functionality would go here');
  }, []);

  const handleSearchChange = useCallback((searchTerm) => {
    dispatch({ type: 'SET_SEARCH_TERM', payload: searchTerm });
  }, []);

  const handleGridLayoutChange = useCallback((layout) => {
    dispatch({ type: 'SET_GRID_LAYOUT', payload: layout });
    updateNestedPreference('monitoring.gridLayout', layout);
  }, [updateNestedPreference]);

  const handleStreamReady = useCallback((cameraId) => {
    console.log(`🟢 Stream ready for camera ${cameraId}`);
    dispatch({ type: 'ADD_STREAMING_CAMERA', payload: cameraId });
  }, []);

  const handleStreamError = useCallback((cameraId, error) => {
    console.error(`🔴 Stream error for camera ${cameraId}:`, error);
    dispatch({ type: 'REMOVE_STREAMING_CAMERA', payload: cameraId });
  }, []);

  // Performance metrics for development
  const performanceMetrics = useMemo(() => {
    if (process.env.NODE_ENV !== 'development') return null;
    
    return {
      totalCameras: state.cameras.length,
      filteredCameras: filteredCameras.length,
      activeStreams: state.streamingCameras.size,
      memoryUsage: navigator.deviceMemory || 'unknown',
      connection: navigator.connection?.effectiveType || 'unknown'
    };
  }, [state.cameras.length, filteredCameras.length, state.streamingCameras.size]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading cameras...</p>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Cameras</h2>
          <p className="text-gray-600 mb-4">{state.error}</p>
          <button
            onClick={loadCameras}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Monitor className="h-8 w-8 text-indigo-600 mr-3" />
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Camera Monitoring</h1>
                <p className="text-sm text-gray-500">
                  {filteredCameras.length} of {state.cameras.length} cameras
                  {state.streamingCameras.size > 0 && ` • ${state.streamingCameras.size} streaming`}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Grid Layout Selector */}
              <div className="flex items-center space-x-2">
                <Layout className="h-4 w-4 text-gray-400" />
                <select
                  value={state.gridLayout}
                  onChange={(e) => handleGridLayoutChange(e.target.value)}
                  className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="auto">Auto Grid</option>
                  <option value="2x2">2×2 Grid</option>
                  <option value="3x3">3×3 Grid</option>
                  <option value="4x4">4×4 Grid</option>
                  <option value="5x5">5×5 Grid</option>
                </select>
              </div>

              <Link
                to="/cameras"
                className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
              >
                <Settings className="h-4 w-4" />
                <span>Manage</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Breadcrumb */}
      <BreadcrumbNavigation />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="space-y-6">
          {/* Performance Info (Development Only) */}
          {performanceMetrics && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <h3 className="text-sm font-medium text-yellow-800 mb-2">Performance Metrics (Dev)</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs text-yellow-700">
                <div>Cameras: {performanceMetrics.totalCameras}</div>
                <div>Filtered: {performanceMetrics.filteredCameras}</div>
                <div>Streams: {performanceMetrics.activeStreams}</div>
                <div>Memory: {performanceMetrics.memoryUsage}GB</div>
                <div>Network: {performanceMetrics.connection}</div>
              </div>
            </div>
          )}

          {/* Optimized Camera Grid */}
          <SimplifiedCameraGrid
            cameras={filteredCameras}
            searchTerm={state.searchTerm}
            onSearchChange={handleSearchChange}
            onCameraClick={handleCameraClick}
            onEditCamera={hasPermission('camera:update') ? handleEditCamera : null}
            onDeleteCamera={hasPermission('camera:delete') ? handleDeleteCamera : null}
            onTestConnection={hasPermission('camera:test') ? handleTestConnection : null}
            onAssignCamera={hasPermission('camera:assign') ? handleAssignCamera : null}
            hasPlaybackPermission={hasPermission('camera:playback')}
            thumbnailKeys={state.thumbnailKeys}
            gridLayout={state.gridLayout}
          />
        </div>
      </main>

      {/* Fullscreen Stream Modal */}
      {state.showFullscreenModal && state.selectedCamera && (
        <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50">
          <div className="bg-black rounded-lg overflow-hidden w-full max-w-6xl mx-4 max-h-[95vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-gray-900 text-white">
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${state.selectedCamera.isOnline ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <h2 className="text-lg font-semibold">{state.selectedCamera.name}</h2>
                <span className="text-sm text-gray-400">
                  {state.selectedCamera.ipAddress}:{state.selectedCamera.port}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleCloseFullscreen}
                  className="p-2 hover:bg-gray-800 rounded transition-colors"
                  title="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Video Container */}
            <div className="flex-1 bg-black">
              <div className="aspect-video bg-gray-900 relative">
                <OptimizedCameraStream
                  camera={state.selectedCamera}
                  onStreamReady={handleStreamReady}
                  onStreamError={handleStreamError}
                  size="fullscreen"
                  autoStart={true}
                  priority={100}
                  adaptiveQuality={true}
                />
              </div>
            </div>

            {/* Camera Info */}
            <div className="p-4 bg-gray-900 text-white">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-400">Type:</span>
                  <span className="ml-2">{state.selectedCamera.type?.toUpperCase()}</span>
                </div>
                <div>
                  <span className="text-gray-400">Location:</span>
                  <span className="ml-2">{state.selectedCamera.location?.exactLocation || 'Not specified'}</span>
                </div>
                <div>
                  <span className="text-gray-400">Status:</span>
                  <span className={`ml-2 ${state.selectedCamera.isOnline ? 'text-green-400' : 'text-red-400'}`}>
                    {state.selectedCamera.isOnline ? 'Online' : 'Offline'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400">Quality:</span>
                  <span className="ml-2">Adaptive</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating Controls */}
      <FloatingControls>
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="p-3 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition-colors shadow-lg"
          title="Scroll to top"
        >
          <Maximize2 className="h-5 w-5" />
        </button>
      </FloatingControls>
    </div>
  );
};

export default OptimizedCameraMonitoring;